import React from 'react'
import { NavLink } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  LayoutDashboard,
  Server,
  ScrollText,
  Container,
  Cpu,
  Globe,
  HardDrive,
  Activity,
  Layers,
  Users,
  Wallet,
  TrendingUp,
  CreditCard,
  ShieldCheck,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  ChevronUp,
  LogOut,
} from 'lucide-react'
import { cn } from '@/core/analytics_utils'
import { Button, Text } from '@agion/ui'
import { getConfig } from '@/core/analytics_runtime_env'
import { SUPPORTED_LANGUAGES, languageDisplayName } from '@/core/analytics_i18n'
import AgionLogo from '@/assets/agion-logo.svg'
import AgionLogoDark from '@/assets/agion-logo-dark.svg'
import { AnalyticsAppSwitcher } from '@/components/AnalyticsAppSwitcher'

interface NavItem {
  nameKey: string
  href?: string
  icon: React.ComponentType<{ className?: string }>
  children?: NavItem[]
  // Force exact-match active styling (for section-root links that are a prefix
  // of their siblings, e.g. /infrastructure vs /infrastructure/services).
  end?: boolean
}

const navigation: NavItem[] = [
  {
    nameKey: 'infrastructure',
    icon: HardDrive,
    children: [
      { nameKey: 'overview', href: '/infrastructure', icon: LayoutDashboard, end: true },
      { nameKey: 'services', href: '/infrastructure/services', icon: Server },
      { nameKey: 'logs', href: '/infrastructure/logs', icon: ScrollText },
      { nameKey: 'kubernetes', href: '/infrastructure/kubernetes', icon: Container },
      { nameKey: 'gpu', href: '/infrastructure/gpu', icon: Cpu },
      { nameKey: 'gateway', href: '/infrastructure/gateway', icon: Globe },
      { nameKey: 'infraAssets', href: '/infrastructure/assets', icon: ShieldCheck },
    ],
  },
  {
    nameKey: 'usage',
    icon: Activity,
    children: [
      { nameKey: 'aggregated', href: '/usage/aggregated', icon: Layers },
      { nameKey: 'perCustomer', href: '/usage/per-customer', icon: Users },
    ],
  },
  {
    nameKey: 'accounting',
    icon: Wallet,
    children: [
      { nameKey: 'revenue', href: '/accounting/revenue', icon: TrendingUp },
      { nameKey: 'spend', href: '/accounting/spend', icon: CreditCard },
    ],
  },
]

const NavSection: React.FC<{
  item: NavItem
  collapsed: boolean
}> = ({ item, collapsed }) => {
  const { t } = useTranslation('pages')
  const [isOpen, setIsOpen] = React.useState(true)

  const hasChildren = item.children && item.children.length > 0

  // Single item without children -- direct link
  if (!hasChildren && item.href) {
    return (
      <NavLink
        to={item.href}
        end={item.href === '/'}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-300 dark:focus-visible:ring-slate-600',
            isActive
              ? 'bg-slate-100 text-slate-900 dark:bg-slate-700/50 dark:text-slate-100'
              : 'text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700/30'
          )
        }
        title={collapsed ? t(`nav.${item.nameKey}`) : undefined}
      >
        <item.icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
        {!collapsed && <span className="flex-1">{t(`nav.${item.nameKey}`)}</span>}
      </NavLink>
    )
  }

  // Collapsed mode -- just show child icons
  if (collapsed) {
    return (
      <div className="space-y-1">
        {item.children?.map((child) => (
          <NavLink
            key={child.nameKey}
            to={child.href!}
            end={child.end ?? child.href === '/'}
            aria-label={t(`nav.${child.nameKey}`)}
            className={({ isActive }) =>
              cn(
                'flex items-center justify-center rounded-md p-2 transition-colors',
                isActive
                  ? 'bg-slate-100 text-slate-900 dark:bg-slate-700/50 dark:text-slate-100'
                  : 'text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-700/30'
              )
            }
            title={t(`nav.${child.nameKey}`)}
          >
            <child.icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
          </NavLink>
        ))}
      </div>
    )
  }

  // Expanded -- segmented section with header + children
  return (
    <div className="space-y-0.5">
      <Button
        variant="ghost"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors h-auto"
      >
        <span className="flex-1 text-left">{t(`nav.${item.nameKey}`)}</span>
        {isOpen ? (
          <ChevronDown className="h-3.5 w-3.5" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5" />
        )}
      </Button>

      {isOpen && item.children && (
        <div className="space-y-0.5">
          {item.children.map((child) => (
            <NavLink
              key={child.nameKey}
              to={child.href!}
              end={child.end ?? child.href === '/'}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 rounded-md px-3 py-1.5 text-sm transition-colors',
                  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-300 dark:focus-visible:ring-slate-600',
                  isActive
                    ? 'bg-slate-100 text-slate-900 dark:bg-slate-700/50 dark:text-slate-100 font-medium'
                    : 'text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-700/30'
                )
              }
            >
              <child.icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              <span className="flex-1">{t(`nav.${child.nameKey}`)}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  )
}

interface SidebarProps {
  collapsed: boolean
  collapseLocked: boolean
  onToggleCollapse: () => void
}

export const Sidebar: React.FC<SidebarProps> = ({ collapsed, collapseLocked, onToggleCollapse }) => {
  const { t, i18n } = useTranslation('pages')
  const isDark = document.documentElement.classList.contains('dark')
  const [langMenuOpen, setLangMenuOpen] = React.useState(false)

  const languages = SUPPORTED_LANGUAGES.map((code) => ({
    code,
    name: languageDisplayName(code),
  }))

  const currentLanguage = languages.find(l => l.code === i18n.language) ?? languages[0]

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng)
    localStorage.setItem('preferredLanguage', lng)
    setLangMenuOpen(false)
  }

  const handleLogout = () => {
    const loginUrl = getConfig('VITE_LOGIN_URL', 'https://login.dev.agion.dev')
    window.location.href = `${loginUrl}/logout`
  }

  return (
    <div className="flex h-full flex-col">
      {/* Logo */}
      <div className="flex items-center justify-between h-14 pl-5 pr-3 mb-1">
        <div className="flex items-center gap-2.5">
          {!collapsed && (
            <img
              src={isDark ? AgionLogoDark : AgionLogo}
              alt="Agion"
              className="h-5 w-auto"
            />
          )}
        </div>
        {!collapseLocked && (
          <Button
            variant="ghost"
            onClick={onToggleCollapse}
            className="p-1.5 h-auto text-slate-500"
            aria-label={collapsed ? t('nav.expandSidebar', 'Expand sidebar') : t('nav.collapseSidebar', 'Collapse sidebar')}
          >
            <ChevronLeft className={cn('h-4 w-4 transition-transform', collapsed && 'rotate-180')} />
          </Button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-2 py-1 space-y-3" aria-label={t('nav.mainNavigation', 'Main navigation')}>
        {navigation.map((item) => (
          <NavSection key={item.nameKey} item={item} collapsed={collapsed} />
        ))}
      </nav>

      {/* Footer */}
      <div className="border-t border-slate-200 dark:border-slate-700/50 p-2 space-y-1">
        <AnalyticsAppSwitcher collapsed={collapsed} />

        {/* Language Switcher */}
        <div
          role="button"
          tabIndex={0}
          className={cn(
            'flex items-center gap-2 rounded-md px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors cursor-pointer',
            collapsed && 'justify-center px-0'
          )}
          onClick={() => setLangMenuOpen(!langMenuOpen)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setLangMenuOpen(!langMenuOpen) } }}
        >
          <Globe className="h-5 w-5 text-slate-600 dark:text-slate-400 flex-shrink-0" />
          {!collapsed && (
            <>
              <Text as="span" className="flex-1 text-slate-700 dark:text-slate-300 text-sm">
                {currentLanguage?.name}
              </Text>
              {langMenuOpen ? (
                <ChevronUp className="h-4 w-4 text-slate-500 dark:text-slate-400" />
              ) : (
                <ChevronDown className="h-4 w-4 text-slate-500 dark:text-slate-400" />
              )}
            </>
          )}
        </div>

        {langMenuOpen && !collapsed && (
          <div className="ml-7 mb-2 space-y-1">
            {languages.map((lang) => (
              <Button
                key={lang.code}
                variant="ghost"
                onClick={() => changeLanguage(lang.code)}
                className={cn(
                  'w-full justify-start gap-2 px-3 py-1.5 text-sm',
                  lang.code === i18n.language
                    ? 'bg-slate-100 text-slate-900 dark:bg-slate-700/50 dark:text-slate-100'
                    : 'text-slate-600 dark:text-slate-400'
                )}
              >
                <span>{lang.name}</span>
              </Button>
            ))}
          </div>
        )}

        {/* Logout */}
        <div
          role="button"
          tabIndex={0}
          className={cn(
            'flex items-center gap-2 rounded-md px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors cursor-pointer',
            collapsed && 'justify-center px-0'
          )}
          onClick={handleLogout}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleLogout() } }}
        >
          <LogOut className="h-5 w-5 text-slate-600 dark:text-slate-400 flex-shrink-0" />
          {!collapsed && (
            <Text as="span" className="text-slate-700 dark:text-slate-300 text-sm">
              {t('nav.logout', 'Logout')}
            </Text>
          )}
        </div>
      </div>
    </div>
  )
}
